import {
  useCompaniesWithNoTicketsLastMonth,
  useCompaniesWithNoTicketsThisMonth,
  useCompaniesWithTicketsLastMonth,
  useCompaniesWithTicketsThisMonth,
} from "@/lib/hooks/api/companies";
import type { SimpleTableConfig, SavedViewConfig } from "@/components/shared/UniversalCRMTable/types";
import type { CompanyEntity } from "@/lib/types/company";
import { COMPANY_FIELDS, type CompanyFieldId } from "../company-field-ids";
import { collectFieldsForSavedView } from "./collect-fields-for-saved-view";

/**
 * `visibleColumns` for each saved view is derived from `savedViewMembership`
 * declared on each field in `config/fields/*.ts`. This keeps field
 * declarations the single source of truth for which views include them — no
 * need to update both a field file and this file when toggling membership.
 */
const visible = (viewId: string): CompanyFieldId[] =>
  collectFieldsForSavedView<CompanyFieldId>(viewId, COMPANY_FIELDS);

export const companySavedViews: SavedViewConfig<CompanyFieldId>[] = [
  {
    id: "all-companies",
    name: "All Companies",
    filters: {},
    sort: { field: "created_at", direction: "desc" },
    visibleColumns: visible("all-companies"),
  },
  {
    id: "recent-companies",
    name: "Recent Companies",
    filters: {},
    sort: { field: "created_at", direction: "desc" },
    visibleColumns: visible("recent-companies"),
  },
  // Status-based views
  {
    id: "status-active",
    name: "Active",
    filters: { status: "active" },
    sort: { field: "created_at", direction: "desc" },
    visibleColumns: visible("status-active"),
  },
  {
    id: "status-inactive",
    name: "Inactive",
    filters: { status: "inactive" },
    sort: { field: "created_at", direction: "desc" },
    visibleColumns: visible("status-inactive"),
  },
  {
    id: "status-prospect",
    name: "Prospect",
    filters: { status: "prospect" },
    sort: { field: "created_at", direction: "desc" },
    visibleColumns: visible("status-prospect"),
  },
  {
    id: "status-client",
    name: "Client",
    filters: { status: "client" },
    sort: { field: "created_at", direction: "desc" },
    visibleColumns: visible("status-client"),
  },
  {
    id: "clients-with-active-deals",
    name: "Clients With Active Deals",
    filters: { status: "client", has_active_deals: true },
    sort: { field: "active_deal_count", direction: "desc" },
    visibleColumns: visible("clients-with-active-deals"),
  },
  {
    id: "clients-without-active-deals",
    name: "Clients Without Active Deals",
    filters: { status: "client", has_active_deals: false },
    sort: { field: "updated_at", direction: "desc" },
    visibleColumns: visible("clients-without-active-deals"),
  },
  {
    id: "status-former-client",
    name: "Former Client",
    filters: { status: "former_client" },
    sort: { field: "created_at", direction: "desc" },
    visibleColumns: visible("status-former-client"),
  },
  {
    id: "status-suspended",
    name: "Suspended",
    filters: { status: "suspended" },
    sort: { field: "created_at", direction: "desc" },
    visibleColumns: visible("status-suspended"),
  },
  // Ticket-based views
  {
    id: "no-tickets-this-month",
    name: "No Tickets This Month",
    filters: {}, // Filters applied via custom hook in UniversalEntityPage
    sort: { field: "name", direction: "asc" },
    visibleColumns: visible("no-tickets-this-month"),
  },
  {
    id: "with-tickets-this-month",
    name: "With Tickets This Month",
    filters: {}, // Filters applied via custom hook in UniversalEntityPage
    sort: { field: "name", direction: "asc" },
    visibleColumns: visible("with-tickets-this-month"),
  },
  {
    id: "no-tickets-last-month",
    name: "No Tickets Last Month",
    filters: {}, // Filters applied via custom hook in UniversalEntityPage
    sort: { field: "name", direction: "asc" },
    visibleColumns: visible("no-tickets-last-month"),
  },
  {
    id: "with-tickets-last-month",
    name: "With Tickets Last Month",
    filters: {}, // Filters applied via custom hook in UniversalEntityPage
    sort: { field: "name", direction: "asc" },
    visibleColumns: visible("with-tickets-last-month"),
  },
  // Contract-coverage views. Backed by the trigger-maintained
  // companies.has_signed_contract / contract_expires_at columns
  // (migration 20260818160000_crm_derived_rollups.sql).
  {
    id: "under-contract",
    name: "Under Contract",
    filters: { has_signed_contract: true },
    sort: { field: "contract_expires_at", direction: "asc" },
    visibleColumns: visible("under-contract"),
  },
  {
    id: "no-contract",
    name: "No Contract",
    filters: { has_signed_contract: false },
    sort: { field: "name", direction: "asc" },
    visibleColumns: visible("no-contract"),
  },
  {
    // Window is expressed in days rather than a stored "expiring" flag so it
    // stays correct without a nightly job — see the
    // `contract_expiring_within_days` virtual filter field.
    id: "contract-expiring-soon",
    name: "Renewals Due (90 Days)",
    filters: { filter_contract_expiring_within_days: 90 },
    sort: { field: "contract_expires_at", direction: "asc" },
    visibleColumns: visible("contract-expiring-soon"),
  },
  // Live support load, as opposed to the month-scoped ticket views above.
  {
    id: "with-open-tickets",
    name: "With Open Tickets",
    filters: { has_open_tickets: true },
    sort: { field: "open_ticket_count", direction: "desc" },
    visibleColumns: visible("with-open-tickets"),
  },
  {
    id: "cold-90-days",
    name: "Cold 90+ Days",
    filters: { filter_not_contacted_within_days: 90 },
    sort: { field: "last_contacted_at", direction: "asc" },
    visibleColumns: visible("cold-90-days"),
  },
];

export const companyCustomFilterHooks: NonNullable<SimpleTableConfig<CompanyEntity>["customFilterHooks"]> = {
  "no-tickets-this-month": useCompaniesWithNoTicketsThisMonth,
  "with-tickets-this-month": useCompaniesWithTicketsThisMonth,
  "no-tickets-last-month": useCompaniesWithNoTicketsLastMonth,
  "with-tickets-last-month": useCompaniesWithTicketsLastMonth,
};
